import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import { ApiHelper } from "../../utils/ApiHelper";
import { ADMIN_DASHBOARD_STATS } from "../../lib/API_KEYS";
import Projects from "../../components/Project/Projects";
import ActivityNotifications from "../../components/Dashboard/ActivityNotifications";

const Dashboard = () => {
  const navigate = useNavigate();
  const boxBackground = "/images/background.svg";

  const [username, setUsername] = useState("");
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);


  const today = new Date();
  const formattedDate = today.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const fullName = user.attributes.name;
        const first = fullName.split(" ")[0];
        setUsername(first);
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };
    fetchUser();

    const fetchDashboardStats = async () => {
      try {
        const res = await new ApiHelper().FetchFromServer(...ADMIN_DASHBOARD_STATS);
        if (!res?.isError) {
          setDashboardStats(res.data);
        }
      } catch (err) {
        console.error("Error fetching dashboard stats:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardStats();
  }, []);

  const handleMetricClick = (section, metricLabel) => {
    const navigationMap = {
      'Registration': {
        'All Companies': { path: '/admin-dashboard/buyers' },
        'In progress': { path: '/admin-dashboard/buyers', state: { statusFilter: 'In Review' } },
        'Approved': { path: '/admin-dashboard/buyers', state: { statusFilter: 'Approved' } },
        'Rejected': { path: '/admin-dashboard/buyers', state: { statusFilter: 'Rejected' } },
        'On hold': { path: '/admin-dashboard/buyers', state: { statusFilter: 'On Hold,Query Raised' } }
      },
      'RFQ': {
        'All RFQs': { path: '/admin-dashboard/rfqs' },
        'In review': { path: '/admin-dashboard/rfqs', state: { statusFilter: 'Requirement Posted' } },
        'Accepted': { path: '/admin-dashboard/rfqs', state: { statusFilter: 'Seller Intimation' } },
        'Rejected': { path: '/admin-dashboard/rfqs', state: { statusFilter: 'Expired' } }
      },
      'Projects': {
        'All Projects': { path: '/admin-dashboard/projects' },
        'In Execution': { path: '/admin-dashboard/projects', state: { statusOrderFilter: '10-11' } },
        'Completed': { path: '/admin-dashboard/projects', state: { statusOrderFilter: '12' } }
      }
    };

    const target = navigationMap[section]?.[metricLabel];
    if (target) {
      navigate(target.path, { state: target.state });
    }
  };

  const sections = dashboardStats ? [
    {
      title: "Registration",
      items: [
        { label: "All Companies", value: dashboardStats.registration.total, icon: "/images/rfq.svg" },
        { label: "In progress", value: dashboardStats.registration.inProgress, icon: "/images/progress.svg" },
        { label: "Approved", value: dashboardStats.registration.approved, icon: "/images/fileUpload.svg" },
        { label: "Rejected", value: dashboardStats.registration.rejected, icon: "/images/rejected.svg" },
        { label: "On hold", value: dashboardStats.registration.onHold, icon: "/images/onHold.svg" },
      ],
    },
    {
      title: "RFQ",
      items: [
        { label: "All RFQs", value: dashboardStats.rfq.total, icon: "/images/buyer.svg" },
        { label: "In review", value: dashboardStats.rfq.inReview, icon: "/images/review.svg" },
        { label: "Accepted", value: dashboardStats.rfq.accepted, icon: "/images/accepted.svg" },
        { label: "Rejected", value: dashboardStats.rfq.rejected, icon: "/images/rfqRejected.svg" },
      ],
    },
    {
      title: "Projects",
      items: [
        { label: "All Projects", value: dashboardStats.projects.total, icon: "/images/project.svg" },
        { label: "In Execution", value: dashboardStats.projects.inExecution, icon: "/images/execution.svg" },
        { label: "Completed", value: dashboardStats.projects.completed, icon: "/images/completed.svg" },
      ],
    },
  ] : [];

  return (
    <>
      <div className="container my-4">
        <div className="row align-items-center justify-content-between">
          <div className="col-6 d-flex align-items-center">
            <img
              src="/images/hand.svg"
              alt="wave"
              className="me-2"
              style={{ width: "24px", height: "24px" }}
            />
            <h4 className="mb-0" style={{ maxWidth: "100%" }}>
              Hello, {username}
            </h4>
          </div>

          <div className="col-6 d-flex justify-content-end">
            <div
              className="d-inline-flex align-items-center px-3 py-2 text-nowrap flex-shrink-0"
              style={{
                border: "1px solid #dee2e6",
                borderRadius: "50px",
                backgroundColor: "#f8f9fa",
              }}
            >
              <img
                src="/images/calendar.svg"
                alt="Calendar"
                style={{ width: "24px", height: "24px", marginRight: "8px" }}
              />
              <span>{formattedDate}</span>
            </div>
          </div>

          <div className="col-12 mt-2">
            <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
              Your personalized dashboard for smarter control and seamless
              experience
            </p>
          </div>
        </div>
      </div>

      <div className="container-fluid p-4">
        {sections.map((section, sIdx) => (
          <div key={sIdx} className="mb-5">
            <h5 className="mb-3">{section.title}</h5>
            <div className="d-flex flex-wrap" style={{ gap: "1rem" }}>
              {section.items.map((item, idx) => (
                <div
                  key={idx}
                  className="flex-fill p-4 rounded-4 border shadow-sm text-start"
                  onClick={() => handleMetricClick(section.title, item.label)}
                  style={{
                    backgroundImage: `url(${boxBackground})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    minHeight: "150px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    position: "relative",
                    cursor: "pointer",
                    transition: "transform 0.2s, box-shadow 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "";
                  }}
                >
                  <div style={{ position: "relative", zIndex: 1 }}>
                    {item.icon && (
                      <img
                        src={item.icon}
                        alt="icon"
                        style={{ width: "30px", marginBottom: "8px" }}
                      />
                    )}
                    <h4 className="mb-2">{item.value}</h4>
                    <p className="mb-0 text-muted">{item.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="container-fluid p-4">
        <h5 className="mb-3">Contracts</h5>
        <Projects userRole="Admin" viewType="contracts" />
      </div>

      <div className="container-fluid p-4">
        <h5 className="mb-3">Projects</h5>
        <Projects userRole="Admin" viewType="projects" />
      </div>

      <div className="container-fluid px-4">
        <ActivityNotifications userRole="admin" />
      </div>
    </>
  );
};

export default Dashboard;
